import { RESERVED_SLUGS, SLUG_PATTERN, slugify } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Building2, Globe2, Loader2, Pencil } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import { ErrorPanel } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/misc";
import { AccountFrame, InvitationList } from "@/features/accounts/shared";
import { ApiError, q, useSignup } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/signup")({
  component: SignupPage,
});

const REGIONS = [
  { id: "us", label: "United States", help: "Data stored and processed in the US" },
  { id: "eu", label: "European Union", help: "Data stored and processed in the EU" },
] as const;

function SignupPage() {
  const me = useQuery(q.me());
  const navigate = useNavigate();
  const signup = useSignup();
  const ids = { name: useId(), slug: useId() };
  const [name, setName] = useState("");
  const [customSlug, setCustomSlug] = useState<string | null>(null);
  const [region, setRegion] = useState<"us" | "eu">("us");
  const [touched, setTouched] = useState(false);
  const [slugTaken, setSlugTaken] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Create your organization · traces.insure";
  }, []);

  const derived = slugify(name);
  const slug = customSlug ?? derived;
  const nameValid = name.trim().length >= 2;
  const slugValid = customSlug === null ? true : SLUG_PATTERN.test(customSlug) && !RESERVED_SLUGS.has(customSlug);
  const current = me.data?.orgs.find((o) => o.id === me.data?.currentOrgId) ?? me.data?.orgs[0];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!nameValid || !slugValid) return;
    signup.mutate(
      { orgName: name.trim(), dataRegion: region, ...(customSlug !== null ? { slug: customSlug } : {}) },
      {
        onSuccess: () => void navigate({ to: "/" }),
        onError: (err) => setSlugTaken(err instanceof ApiError && err.code === "slug_taken" ? slug : null),
      },
    );
  };

  return (
    <AccountFrame
      aside={
        me.data ? (
          <span className="truncate text-xs text-fg-3">
            Signed in as <span className="text-fg-2">{me.data.email}</span>
          </span>
        ) : null
      }
    >
      {me.error ? (
        <ErrorPanel error={me.error} reset={() => void me.refetch()} />
      ) : !me.data ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {current ? (
            <Button variant="link" size="sm" asChild className="self-start">
              <Link to="/">
                <ArrowLeft /> Back to {current.name}
              </Link>
            </Button>
          ) : null}

          {me.data.invitations.length ? (
            <section aria-labelledby="signup-invitations" className="flex flex-col gap-3">
              <div>
                <h1 id="signup-invitations" className="text-lg font-semibold tracking-tight">
                  You've been invited
                </h1>
                <p className="text-xs text-fg-3">Join your team's workspace, or create a new organization below.</p>
              </div>
              <InvitationList invitations={me.data.invitations} onAccepted={() => void navigate({ to: "/" })} />
            </section>
          ) : null}

          <section aria-labelledby="signup-title" className="flex flex-col gap-3">
            <div>
              <h1 id="signup-title" className="text-lg font-semibold tracking-tight">
                {me.data.orgs.length ? "Create another organization" : me.data.invitations.length ? "Or create your own organization" : "Create your organization"}
              </h1>
              <p className="text-xs text-fg-3">An organization holds your agents, traces, issues and coverage. You'll be its owner and can invite your team next.</p>
            </div>
            <Card>
              <CardHeader title="Organization" description="You can rename it later in Settings; the URL slug and data region are fixed once created." />
              <CardBody>
                <form onSubmit={submit} noValidate className="flex flex-col gap-5">
                  <div>
                    <Label htmlFor={ids.name}>Organization name</Label>
                    <Input id={ids.name} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Outfitters" maxLength={120} autoFocus aria-invalid={touched && !nameValid} />
                    {touched && !nameValid ? <p className="mt-1 text-[11px] text-critical-text">Enter at least 2 characters.</p> : null}
                  </div>

                  <div>
                    <Label htmlFor={ids.slug}>URL slug</Label>
                    {customSlug === null ? (
                      <div className="flex h-8 items-center gap-2 rounded-md border border-dashed px-2.5 text-[13px]">
                        <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-fg-2">
                          traces.insure/protect/<span className="text-fg">{derived || "your-org"}</span>
                        </span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setCustomSlug(derived)} aria-label="Customize the URL slug">
                          <Pencil /> Customize
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[12px] text-fg-3">traces.insure/protect/</span>
                        <Input id={ids.slug} value={customSlug} onChange={(e) => setCustomSlug(e.target.value.toLowerCase())} className="font-mono text-[12.5px]" maxLength={50} spellCheck={false} aria-invalid={!slugValid || slugTaken === slug} />
                        <Button type="button" variant="ghost" size="sm" onClick={() => setCustomSlug(null)}>
                          Use default
                        </Button>
                      </div>
                    )}
                    {!slugValid ? (
                      <p className="mt-1 text-[11px] text-critical-text">{customSlug && RESERVED_SLUGS.has(customSlug) ? "This slug is reserved." : "Lowercase letters, digits and dashes; starts and ends with a letter or digit."}</p>
                    ) : slugTaken === slug ? (
                      <p className="mt-1 text-[11px] text-critical-text">This slug is taken. Pick another one.</p>
                    ) : (
                      <p className="mt-1 text-[11px] text-fg-3">Used for your public protection page.{customSlug === null ? " If it's taken, a numbered variant is used." : ""}</p>
                    )}
                  </div>

                  <fieldset>
                    <legend className="mb-1 text-xs font-medium text-fg-2">Data region</legend>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {REGIONS.map((r) => (
                        <label key={r.id} className={cn("flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ring", region === r.id && "border-accent bg-accent-soft/40")}>
                          <input type="radio" name="data-region" className="sr-only" checked={region === r.id} onChange={() => setRegion(r.id)} />
                          <Globe2 className={cn("mt-0.5 size-3.5 shrink-0", region === r.id ? "text-accent" : "text-fg-3")} aria-hidden />
                          <span>
                            <span className="block text-[13px] font-medium">
                              {r.label} <span className="font-mono text-[11px] uppercase text-fg-3">{r.id}</span>
                            </span>
                            <span className="block text-[11px] text-fg-3">{r.help}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className="flex justify-end border-t pt-4">
                    <Button type="submit" disabled={signup.isPending}>
                      {signup.isPending ? <Loader2 className="animate-spin" /> : <Building2 />}
                      Create organization
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </section>
        </div>
      )}
    </AccountFrame>
  );
}
